import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  OutlinedInput,
  Stack,
  useTheme,
} from "@mui/material";
import { Field, FieldsContainer, Spacer, TextField } from "common";
import { QRCodeSVG } from "qrcode.react";
import { useRef, useState } from "react";
import { SharingFormItem } from "./sharingApi";
import Socials from "./tools/Socials";
import WaiverWidget from "./tools/WaiverWidget";
import WebButton from "./tools/WebButton";

export interface SharingFormProps {
  sharing: SharingFormItem;
  onClose: Function | undefined;
}

export default function SharingTools({ sharing, onClose }: SharingFormProps) {
  const url = `${window.location.origin}/sharing/${sharing.token}`;

  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(600);
  const code = `<iframe src="${url}" title="description" width="${width} height="${height}"></iframe>`;
  const theme = useTheme();

  const qrCodeRef = useRef<SVGSVGElement | null>(null);

  function downloadQrCodeFile() {
    if (qrCodeRef.current) {
      const element = document.createElement("a");

      const file = new Blob(
        [
          qrCodeRef.current?.innerHTML.replace(
            "<svg",
            '<svg xmlns="http://www.w3.org/2000/svg"'
          ),
        ],
        {
          type: "image/svg+xml",
        }
      );
      element.href = URL.createObjectURL(file);
      element.download = `Qr Code ${sharing.name}.svg`;
      document.body.appendChild(element); // Required for this to work in FireFox
      element.click();
    }
  }

  return (
    <Stack spacing={3}>
      <Socials sharingUrl={url} />
      <Card>
        <CardHeader title="Share via url" />
        <CardContent>
          <OutlinedInput value={url} fullWidth multiline readOnly />
        </CardContent>
        <CardActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(url);
            }}
          >
            Copy url
          </Button>
          <Button
            startIcon={<OpenInNewIcon />}
            href={url}
            component={"a"}
            target="_blank"
          >
            Open url
          </Button>
        </CardActions>
      </Card>
      <WaiverWidget waiverId={sharing.token} />
      <WebButton sharingUrl={url} />
      <Card>
        <CardHeader title="Share via QR code" />
        <CardContent>
          <Alert severity="info">
            Read the QR code with your mobile device's Camera application.
          </Alert>
          <Spacer h={1} />
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              ref={qrCodeRef}
              sx={{
                padding: 2,
                flex: 1,
                maxWidth: "256px",
                maxHeight: "256px",
                width: "100%",
                height: "100%",
                //height: "162px",
                //aspectRatio: "1/1",
                borderRadius: theme => `${theme.shape.borderRadius}px`,
                border: theme => `solid 1px ${theme.palette.divider}`,
              }}
            >
              <QRCodeSVG
                value={url || ""}
                size={256}
                bgColor={"#ffffff"}
                fgColor={"#000000"}
                level={"L"}
                includeMargin={false}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                /*imageSettings={{
            src: "favicon.ico",
            x: undefined,
            y: undefined,
            height: 24,
            width: 24,
            excavate: true,
          }}*/
              />
            </Box>
          </Box>
        </CardContent>
        <CardActions>
          <Button
            startIcon={<CloudDownloadIcon />}
            onClick={evt => {
              downloadQrCodeFile();
            }}
          >
            Download QR code
          </Button>
        </CardActions>
      </Card>
      <Card>
        <CardHeader title="Embedded code" />
        <CardContent>
          <FieldsContainer>
            <Field half>
              <TextField
                label="Width"
                value={width}
                useDelayedKeystrokes={false}
                onChange={(evt: any) => setWidth(evt.target.value)}
              />
            </Field>
            <Field half>
              <TextField
                useDelayedKeystrokes={false}
                label="Height"
                value={height}
                onChange={(evt: any) => setHeight(evt.target.value)}
              />
            </Field>
            <Field fullWidth>
              <OutlinedInput value={code} fullWidth multiline readOnly />
            </Field>
          </FieldsContainer>
        </CardContent>
        <CardActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(code);
            }}
          >
            Copy code
          </Button>
        </CardActions>
      </Card>
    </Stack>
  );
}
